import { useAppSelector } from '@hooks/app/redux';
import moment from 'moment';
import { postcodeValidator, postcodeValidatorExistsForCountry } from 'postcode-validator';
import React from 'react';
import { TUserAddress } from 'types/users';
import * as Yup from 'yup';

export interface IProfileEditForm {
  /** Personal */
  firstName: string;
  lastName: string;
  pin: string;
  birthDate: Date | undefined;
  /** Address */
  address: string;
  city: string;
  country: string;
  postalCode: string;
}

export const initialValues: IProfileEditForm = {
  /** Personal */
  firstName: '',
  lastName: '',
  pin: '',
  birthDate: undefined,
  /** Address */
  address: '',
  city: '',
  country: '',
  postalCode: '',
};

const getAddressesData = (addresses: TUserAddress[]) => {
  const permanentAddress = addresses.find((address) => address.type === 'permanent');

  return permanentAddress
    ? {
        city: permanentAddress.city,
        country: permanentAddress.country,
        postalCode: permanentAddress.postCode,
        address: permanentAddress.address,
      }
    : {};
};

export const validationSchema = Yup.object().shape({
  firstName: Yup.string().required('validation.required'),
  lastName: Yup.string().required('validation.required'),
  pin: Yup.string().nullable().required('validation.required'),
  birthDate: Yup.date().nullable().required('validation.required'),
  city: Yup.string()
    .test('required', 'validation.required_field', (city, { parent }) => {
      const address = parent.address;
      const country_code = parent.country;
      const post_code = parent.post_code;

      if (address || country_code || post_code) {
        return Boolean(city);
      }

      return true;
    })
    .max(64, 'validation.max64')
    .nullable()
    .required('validation.required'),
  address: Yup.string()
    .test('required', 'validation.required_field', (address, { parent }) => {
      const city = parent.city;
      const country_code = parent.country;
      const post_code = parent.postalCode;

      if (city || country_code || post_code) {
        return Boolean(address);
      }

      return true;
    })
    .test('validate-street-number', 'validation.address_format', (address) => {
      if (address !== null && address !== undefined) {
        return /\d/.test(address) && /[a-zA-Z]/.test(address);
      }

      return true;
    })
    .max(64, 'validation.max64')
    .nullable()
    .required('validation.required'),
  country: Yup.string()
    .test('required', 'validation.required_field', (country_code, { parent }) => {
      const city = parent.city;
      const address = parent.address;
      const post_code = parent.postalCode;

      if (city || address || post_code) {
        return Boolean(country_code);
      }

      return true;
    })
    .max(3)
    .nullable()
    .required('validation.required'),
  postalCode: Yup.string()
    .test('required', 'validation.required_field', (post_code, { parent }) => {
      const city = parent.city;
      const address = parent.address;
      const country_code = parent.country;

      if (city || address || country_code) {
        return Boolean(post_code);
      }

      return true;
    })
    .test('zip-test', 'validation.zip_format', (zip, testContext) => {
      const country = testContext.parent.country;
      if (!zip || !country || !postcodeValidatorExistsForCountry(country)) {
        return true;
      }

      return postcodeValidator(zip, country);
    })

    .max(16, 'validation.max16')
    .nullable()
    .required('validation.required'),
});

export function useInitialValues() {
  const user = useAppSelector((state) => state.users.currentUser)!;

  const formInitialValues = React.useMemo(() => {
    const res = {
      ...initialValues,
      ...getAddressesData(user.addresses),
      ...user.profile,
      birthDate: user.profile.birthDate
        ? moment(user.profile.birthDate, 'YYYY-MM-DD').toDate()
        : undefined,
    };

    return res;
  }, [user]);

  return formInitialValues;
}
